'use client'

import { ChevronDown, Search } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { LocaleLink } from '@/components/LocaleLink'
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type {
  CategoryRailNavItem,
  ResolvedHeaderNavigation,
} from '@/Header/utilities/resolveHeaderNavigation'

import type { HeaderLanguageData } from './HeaderActions'
import type { getPublicLanguageChromeProps } from '@/modules/languages/components/PublicLanguageChrome'
import { HeaderActions } from './HeaderActions'
import { HeaderBrand } from './HeaderBrand'
import { HeaderCategoryRail } from './HeaderCategoryRail'
import { HeaderMobile } from './HeaderMobile'
import { HeaderSearch } from './HeaderSearch'

type SiteHeaderProps = {
  languageChrome: Awaited<ReturnType<typeof getPublicLanguageChromeProps>>
  siteTitle: string
  logoSrc?: string | null
  logoAlt?: string | null
  logoInvertClassName?: string
  logoWidthPx: number
  nav: ResolvedHeaderNavigation
}

const HOVER_CLOSE_MS = 160

function SuperiorNavHoverDropdown({ item }: { item: CategoryRailNavItem }) {
  const children = item.children ?? []
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const openMenu = useCallback(() => {
    clearCloseTimer()
    setOpen(true)
  }, [clearCloseTimer])

  const scheduleClose = useCallback(() => {
    clearCloseTimer()
    closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_MS)
  }, [clearCloseTimer])

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer])

  return (
    <DropdownMenu
      modal={false}
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) clearCloseTimer()
      }}
    >
      <DropdownMenuTrigger asChild>
        {/* 2026-07-05 (pedido do dono, estilo news.seguro.com): o pai é um LINK — clicar NAVEGA
            para o destino configurado; o submenu abre no hover (openMenu/scheduleClose). */}
        <Button
          variant="link"
          size="clear"
          className="h-9 shrink-0 gap-1 px-3 text-sm font-semibold text-foreground"
          asChild
          aria-expanded={open}
          aria-haspopup="menu"
          onPointerEnter={openMenu}
          onPointerLeave={scheduleClose}
        >
          <LocaleLink href={item.href}>
            {item.label}
            <ChevronDown className="size-3.5 shrink-0 opacity-70" aria-hidden />
          </LocaleLink>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-40"
        sideOffset={2}
        onPointerEnter={openMenu}
        onPointerLeave={scheduleClose}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {/* 2026-07-05: o pai NÃO é mais repetido dentro do dropdown (duplicava «Notícias»);
            só os subitens. A página do pai continua alcançável pelo clique no próprio item. */}
        {children.map((sub) => (
          <DropdownMenuItem key={`${item.href}-${sub.href}-${sub.label}`} asChild>
            <LocaleLink href={sub.href}>{sub.label}</LocaleLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function SiteHeader({
  languageChrome,
  siteTitle,
  logoSrc,
  logoAlt,
  logoInvertClassName,
  logoWidthPx,
  nav,
}: SiteHeaderProps) {
  const t = useTranslations('Header')
  const { login, signup, quickMenu, mobile } = nav.navTop

  // Seletor de idioma (bandeiras) vive no menu, ao lado de "Moeda" (desktop + mobile).
  const languageMenuData: HeaderLanguageData = {
    enabled: languageChrome.enabled && languageChrome.showInHeader,
    defaultLocale: languageChrome.defaultLocale,
    currentLocale: languageChrome.currentLocale,
    languages: languageChrome.languages,
    showFlags: languageChrome.showFlags,
  }

  return (
    <header
      suppressHydrationWarning
      className="surface-chrome sticky top-0 z-30 box-border w-full shrink-0 overflow-visible"
      style={{ boxShadow: '0 .125rem .313rem #1515151f' }}
    >
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-border" aria-hidden />
      <nav
        className="mx-auto flex w-full max-w-(--site-content-width,1350px) flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 pb-1 pt-3 md:min-h-[68px] md:pb-2 lg:grid lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:items-center lg:gap-x-6 lg:gap-y-0 lg:px-6"
        aria-label={t('mainNav')}
      >
        <div className="flex min-h-10 w-fit shrink-0 cursor-pointer items-center">
          <HeaderBrand
            logoAlt={logoAlt}
            logoInvertClassName={logoInvertClassName}
            logoWidthPx={logoWidthPx}
            logoSrc={logoSrc}
            siteTitle={siteTitle}
          />
        </div>

        {/* Desktop: busca centrada na largura útil entre logo e menus/conta */}
        <div className="hidden min-w-0 justify-center justify-self-stretch px-2 lg:flex">
          <Suspense
            fallback={
              <div className="h-8 w-full max-w-[456px] rounded-md bg-muted/50" aria-hidden />
            }
          >
            <HeaderSearch className="w-full max-w-[min(100%,456px)]" />
          </Suspense>
        </div>

        <div className="flex min-w-0 shrink-0 flex-wrap items-center justify-end gap-x-2 gap-y-2">
          {nav.topBarMenuItems.length > 0 ? (
            <div
              className="hidden min-w-0 shrink-0 items-center gap-1 lg:flex"
              aria-label={t('topNav')}
            >
              {nav.topBarMenuItems.map((item, i) =>
                item.children && item.children.length > 0 ? (
                  <SuperiorNavHoverDropdown key={`${item.href}-${item.label}-${i}`} item={item} />
                ) : (
                  <Button
                    key={`${item.href}-${item.label}-${i}`}
                    variant="link"
                    size="clear"
                    className="h-9 shrink-0 px-3 text-sm font-semibold text-foreground"
                    asChild
                  >
                    <LocaleLink href={item.href}>{item.label}</LocaleLink>
                  </Button>
                ),
              )}
            </div>
          ) : null}
          {mobile.showToolbarSearch ? (
            <Button variant="ghost" size="clear" className="size-9 shrink-0 lg:hidden" asChild>
              <LocaleLink
                href={mobile.toolbarSearchHref}
                aria-label={t('search')}
                suppressHydrationWarning
              >
                <Search className="size-5" />
              </LocaleLink>
            </Button>
          ) : null}
          <HeaderActions
            login={login}
            quickMenu={quickMenu}
            signup={signup}
            language={languageMenuData}
          />
          <HeaderMobile
            categoryItems={
              nav.hideNavBottom || nav.hideNavBottomMobileMenu ? [] : nav.navMobileItems
            }
            login={login}
            showSheetSearch={mobile.showSheetSearch}
            showSheetTheme={mobile.showSheetTheme}
            signup={signup}
            superiorNavItems={nav.topBarMenuItems}
            language={languageMenuData}
          />
        </div>
      </nav>

      {!nav.hideNavBottom && nav.navBottomItems.length > 0 && (
        <HeaderCategoryRail items={nav.navBottomItems} />
      )}
    </header>
  )
}
